import { HashRouter, Route, Routes, Navigate } from 'react-router'
import TRPCProvider from './providers/TRPCProvider'
import { useAuthStore } from './stores/auth-store'
import { useEffect } from 'react'
import { useRealtimeConnection } from './hooks/use-realtime'
import Test from './pages/Test'
import Login from './pages/Login'
import Register from './pages/Register'
import Home from './pages/Home'
import Board from './pages/Board'
import Calendar from './pages/Calendar'
import Notes from './pages/Notes'
import Chat from './pages/Chat'
import Teams from './pages/Teams'
import Projects from './pages/Projects'
import Contacts from './pages/Contacts'
import Profile from './pages/Profile'
import Settings from './pages/Settings'
import Pricing from './pages/Pricing'
import Workspace from './pages/Workspace'
import Dashboard from './pages/Dashboard'
import ZeroClickDashboard from './pages/ZeroClickDashboard'
import SmartDashboard from './pages/SmartDashboard'
import UltimateDashboard from './pages/UltimateDashboard'
import Documentation from './pages/Documentation'
import AcceptInvite from './pages/AcceptInvite'
import IntegrationsPage from './pages/IntegrationsPage'
import { Toaster } from './components/ui/toaster'
import { Toaster as Sonner } from './components/ui/sonner'
import GlobalTaskCreationModal from './components/global/GlobalTaskCreationModal'


function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore()
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
          <p>Loading...</p>
        </div>
      </div>
    )
  }
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />
}

export default function App() {
  const { hydrate } = useAuthStore()
  useRealtimeConnection() // Initialize WebSocket connection

  useEffect(() => {
    hydrate().catch(() => {
      // Handle hydration error silently
    })
  }, [hydrate])

  return (
    <TRPCProvider>
      <HashRouter>
        <Routes>
          <Route path="/" element={<Navigate to="/workspace" replace />} />
          <Route path="/test" element={<Test />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/invite/:token" element={<AcceptInvite />} />
          <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
          <Route path="/board" element={<ProtectedRoute><Board /></ProtectedRoute>} />
          <Route path="/calendar" element={<ProtectedRoute><Calendar /></ProtectedRoute>} />
          <Route path="/notes" element={<ProtectedRoute><Notes /></ProtectedRoute>} />
          <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
          <Route path="/teams" element={<ProtectedRoute><Teams /></ProtectedRoute>} />
          <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
          <Route path="/contacts" element={<ProtectedRoute><Contacts /></ProtectedRoute>} />
          <Route path="/integrations/:workspaceId" element={<ProtectedRoute><IntegrationsPage /></ProtectedRoute>} />
          <Route path="/workspace" element={<ProtectedRoute><Workspace /></ProtectedRoute>} />
          <Route path="/workspace/:workspaceId" element={<ProtectedRoute><Workspace /></ProtectedRoute>} />
          <Route path="/dashboard/:workspaceId" element={<ProtectedRoute><UltimateDashboard /></ProtectedRoute>} />
          <Route path="/zero-dashboard/:workspaceId" element={<ProtectedRoute><ZeroClickDashboard /></ProtectedRoute>} />
          <Route path="/smart-dashboard/:workspaceId" element={<ProtectedRoute><SmartDashboard /></ProtectedRoute>} />
          <Route path="/classic-dashboard/:workspaceId" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/workspaces/new" element={<ProtectedRoute><Workspace /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/docs" element={<ProtectedRoute><Documentation /></ProtectedRoute>} />
        </Routes>
      </HashRouter>
      <GlobalTaskCreationModal />
      <Toaster />
      <Sonner position="bottom-center" richColors />
    </TRPCProvider>
  )
}